import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Mail,
  ArrowLeft,
  CheckCircle2,
  Shield,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { emailAPI } from "@/services/api";

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(
      "🔵 [Form] Iniciando proceso de recuperación DIRECTO para:",
      email,
    );
    setIsLoading(true);
    setError(null);

    if (!email) {
      console.warn("⚠️ [Form] Email vacío");
      toast({
        title: "Campo requerido",
        description: "Por favor ingresa tu dirección de email",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      // Llamamos directamente a la API para asegurar que no sea un mock
      const response = await emailAPI.sendPasswordResetEmail(email);
      console.log("🏁 [Form] Respuesta REAL del servidor:", response);

      setSuccess(true);
      toast({
        title: "Enlace enviado",
        description:
          "Revisa tu bandeja de entrada para continuar con el proceso",
        variant: "default",
      });
    } catch (err: any) {
      console.error("❌ [Form] Error real:", err);
      setError(
        err.message || "No se pudo enviar el correo. Intenta nuevamente.",
      );
      toast({
        title: "Error",
        description: err.message || "Error de conexión con el servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen overflow-hidden relative">
      <AnimatedBackground />

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-4">
        <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Image & Branding */}
          <motion.div
            className="hidden md:flex flex-col items-center justify-center space-y-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="relative w-full max-w-2xl">
              <div className="relative">
                <img
                  src="image.png"
                  alt="Sistema Prographic Professional"
                  className="w-full h-auto object-contain drop-shadow-2xl rounded-2xl"
                />
              </div>
            </div>

            <div className="text-center space-y-2">
              <p
                className="text-xl font-semibold"
                style={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Recuperación de Cuenta
              </p>
              <p className="text-sm" style={{ color: "#64748b" }}>
                Restablece tu acceso de forma segura
              </p>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            className="flex flex-col items-center justify-center space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="text-center space-y-4 w-full animate-fade-in-up">
              <div className="space-y-2">
                <h1
                  className="text-3xl md:text-4xl font-black tracking-tight"
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </h1>
                <p
                  className="text-lg font-medium max-w-md mx-auto"
                  style={{ color: "#64748b" }}
                >
                  No te preocupes, te ayudaremos a recuperarla
                </p>
              </div>
            </div>

            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md text-center space-y-6 p-8 bg-white/80 backdrop-blur-md rounded-2xl border border-blue-100 shadow-xl"
              >
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    ¡Enlace Enviado!
                  </h3>
                  <p className="text-gray-600">
                    Hemos enviado las instrucciones de recuperación a:
                  </p>
                  <p className="font-medium text-blue-600 bg-blue-50 py-2 px-4 rounded-lg inline-block">
                    {email}
                  </p>
                </div>

                <Button
                  onClick={handleBackToLogin}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Volver al inicio de sesión
                </Button>
              </motion.div>
            ) : (
              <div className="w-full max-w-md space-y-6">
                {error && (
                  <Alert
                    variant="destructive"
                    className="animate-fade-in-up"
                    style={{
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      borderColor: "rgba(239, 68, 68, 0.2)",
                    }}
                  >
                    <AlertCircle
                      className="h-4 w-4"
                      style={{ color: "#ef4444" }}
                    />
                    <AlertDescription style={{ color: "#ef4444" }}>
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label
                      className="text-sm font-medium"
                      style={{ color: "#334155" }}
                    >
                      Correo Electrónico
                    </Label>
                    <div className="relative group">
                      <div
                        className="absolute inset-0 rounded-xl blur group-focus-within:blur-md transition-all duration-300"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)",
                        }}
                      />
                      <div
                        className="relative bg-white/80 backdrop-blur-md rounded-xl p-3 flex items-center gap-3 border"
                        style={{ borderColor: "rgba(59, 130, 246, 0.2)" }}
                      >
                        <Mail
                          className="w-5 h-5"
                          style={{ color: "#3b82f6" }}
                        />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="ejemplo@empresa.com"
                          className="flex-1 bg-transparent outline-none placeholder:text-gray-400"
                          style={{ color: "#1e293b" }}
                          required
                          disabled={isLoading}
                          autoComplete="email"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !email}
                    className="w-full relative group overflow-hidden rounded-xl"
                  >
                    <div
                      className="absolute inset-0 group-hover:scale-105 transition-transform duration-300"
                      style={{
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                      }}
                    />
                    <div
                      className="relative px-6 py-3 font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all duration-300"
                      style={{ color: "white" }}
                    >
                      {isLoading ? (
                        <>
                          <div
                            className="w-5 h-5 border-2 rounded-full animate-spin"
                            style={{
                              borderColor: "rgba(255, 255, 255, 0.3)",
                              borderTopColor: "white",
                            }}
                          />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-5 h-5" />
                          Enviar Enlace de Recuperación
                        </>
                      )}
                    </div>
                  </button>

                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={handleBackToLogin}
                      className="text-sm font-medium"
                      style={{ color: "#64748b" }}
                      disabled={isLoading}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver al inicio de sesión
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="text-center">
              <p
                className="text-xs flex items-center justify-center"
                style={{ color: "#64748b" }}
              >
                <Shield className="h-3 w-3 mr-1" style={{ color: "#3b82f6" }} />
                Proceso seguro y encriptado
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
